import * as nodeFetch from 'node-fetch';
import * as retry from 'retry';
import { IS_BROWSER_ENVIRONMENT, logVerbose, logVerboseAsync } from '@metaos/mfs-telemetry';
import { logger } from '../logger';
import { isErrorCategory } from '../utils/odspUtils';
const MAX_RETRY_AMOUNT = 3;
const MIN_RETRY_TIMEOUT_MS = 1000;
export var ErrorCategory;
(function (ErrorCategory) {
    ErrorCategory["NoValidOneDriveLicense"] = "NoValidOneDriveLicense";
    ErrorCategory["UserOneDriveNotProvisioned"] = "UserOneDriveNotProvisioned";
    ErrorCategory["Generic"] = "Generic";
})(ErrorCategory || (ErrorCategory = {}));
/**
 * Function to call MfsService and get all required information to enable current user's login experience and setup
 * logger context.
 *
 * @param getMfsAccessToken Callback function for getting accessToken with `MfsServiceScopes`
 * @param sessionId Session id passed to MFS Service to correlate logs
 * @param skipOneDriveDomain Indicator used to determine if the URL is built to skip the retrieval of the OneDriveDomain or not
 * @param mfsServiceDomain Optional domain of MfsService (if not provided the world wide production domain will be used)
 * @returns A `Promise` of the PushChannelInfo object on success, error response otherwise.
 */
export async function getUserPushChannelInfo(getMfsAccessToken, sessionId, skipOneDriveDomain, mfsServiceDomain) {
    return logVerboseAsync(logger, 'getUserPushChannelInfo', async () => {
        try {
            const mfsToken = await getMfsAccessToken();
            const url = getPushChannelInfoUrl(skipOneDriveDomain, mfsServiceDomain);
            const response = await callMfsService(mfsToken, url, sessionId);
            if (response.ok) {
                const responseBody = await response.json();
                logger.logVerbose('getUserPushChannelInfo', 'Success');
                return { pushChannelInfo: responseBody, success: true };
            }
            else {
                const errMsg = `Encountered error when getting PushChannelInfo from Mfs Service: ${response.status} ${response.statusText}`;
                logger.logVerbose('getUserPushChannelInfo', errMsg);
                const errCategory = await getErrorCategory(response);
                return { success: false, error: { message: errMsg, errorCategory: errCategory } };
            }
        }
        catch (error) {
            const errMsg = `Failed to get user PushChannelInfo response: ${error}`;
            logger.logVerbose('getUserPushChannelInfo', errMsg);
            return {
                success: false,
                error: { message: errMsg, errorCategory: ErrorCategory.Generic },
            };
        }
    });
}
/**
 * Function to call MfsService and get all required information to enable current user's client configuration to be
 * consumed by activity tracker for now
 *
 * @param getMfsAccessToken Callback function for getting accessToken with `MfsServiceScopes`
 * @param sessionId Session id passed to MFS Service to correlate logs
 * @param mfsServiceDomain Optional domain of MfsService (if not provided the world wide production domain will be used)
 * @returns A `Promise` of the ClientConfigResponse object
 */
export async function getClientConfig(getMfsAccessToken, sessionId, optionalMfsServiceDomain) {
    return logVerboseAsync(logger, 'getClientConfig', async () => {
        try {
            const mfsToken = await getMfsAccessToken();
            const mfsServiceDomain = getMfsServiceDomain(optionalMfsServiceDomain);
            const url = `https://${mfsServiceDomain}/pushchannel/clientconfig`;
            const response = await callMfsService(mfsToken, url, sessionId);
            if (response.ok) {
                const responseBody = await response.json();
                logger.logVerbose('getClientConfig', 'Client config successfully retrieved');
                return { clientConfig: responseBody, success: true };
            }
            else {
                const errMsg = `Encountered error when getting ClientConfig from Mfs Service: ${response.status} ${response.statusText}`;
                logger.logVerbose('getClientConfig', errMsg);
                const errCategory = await getErrorCategory(response);
                return { success: false, error: { message: errMsg, errorCategory: errCategory } };
            }
        }
        catch (error) {
            const errMsg = `Failed to get user ClientConfig response: ${error}`;
            logger.logVerbose('getClientConfig', errMsg);
            return {
                success: false,
                error: { message: errMsg, errorCategory: ErrorCategory.Generic },
            };
        }
    });
}
/**
 * Returns the appropriate Error Category Enum based on the response.
 *
 * @param response The response object with the error.
 * @returns The Error Category Enumeration that corresponds to the error category.
 */
async function getErrorCategory(response) {
    const errorCode = await extractErrorCode(response);
    if (isErrorCategory(errorCode))
        return errorCode;
    return ErrorCategory.Generic;
}
/**
 * Parses the reponse body as a JSON object and returns the error code.
 *
 * @param response The response object with the error.
 * @returns The error code.
 */
async function extractErrorCode(response) {
    try {
        const errorCode = (await response.json()).error.code;
        return errorCode;
    }
    catch (err) {
        return ErrorCategory.Generic;
    }
}
/**
 * Call and fetch response from MfsService
 *
 * @param mfsAuthToken Access token scoped with `MfsServiceScopes` to be included in Authorization header
 * @param apiUrl API URL
 * @param sessionId Session id passed to MFS Service to correlate logs
 * @returns A `Promise` of fetch response
 */
async function callMfsService(mfsAuthToken, apiUrl, sessionId) {
    return logVerboseAsync(logger, 'callMfsService', async () => {
        // Check if the script is running at browser or node and use compatible fetch API
        // https://stackoverflow.com/questions/32598971/whats-the-purpose-of-if-typeof-window-undefined
        if (!IS_BROWSER_ENVIRONMENT) {
            logger.logVerbose('callMfsService', 'In NodeJs environment', { sessionId, apiUrl });
            // Run in Node environment
            const headers = new nodeFetch.Headers();
            headers.append('Authorization', `Bearer ${mfsAuthToken}`);
            headers.append('SessionId', sessionId);
            return (await nodeFetch.default(apiUrl, {
                method: 'GET',
                headers: headers,
            }));
        }
        else {
            logger.logVerbose('callMfsService', 'In Browser environment', { sessionId });
            const headers = new Headers();
            headers.append('Authorization', `Bearer ${mfsAuthToken}`);
            headers.append('SessionId', sessionId);
            return fetchWithRetry(apiUrl, { method: 'GET', headers: headers });
        }
    });
}
/**
 * Returns URL for calling MfsService's PushChannel Info API
 *
 * @param skipOneDriveDomain Indicator used to determine if the URL is built to skip the retrieval of the OneDriveDomain or not
 * @param specifiedMfsDomain Specified MFS server domain
 * @returns API URL
 */
function getPushChannelInfoUrl(skipOneDriveDomain, specifiedMfsDomain) {
    return logVerbose(logger, 'getPushChannelInfoUrl', () => {
        const mfsServiceDomain = getMfsServiceDomain(specifiedMfsDomain);
        const url = `https://${mfsServiceDomain}/pushchannel/info?skipOneDriveDomain=${skipOneDriveDomain}`;
        logger.logVerbose('getPushChannelInfoUrl', 'Returning pushchannel info URL', { mfsServiceDomain, url });
        return url;
    });
}
/**
 * Returns the MFS Service Domain.
 *
 * @param specifiedDomain The specified domain name
 * @returns The value of `MfsServiceDomain` item in session storage, if any; or `specifiedDomain` if specified, or the
 *   fallback domain.
 */
function getMfsServiceDomain(specifiedDomain) {
    return logVerbose(logger, 'getMfsServiceDomain', () => {
        let domain = getDomain('api.mfs.microsoft.com', specifiedDomain);
        logger.logVerbose('getMfsServiceDomain', 'Domain retrieved', { specifiedDomain, domain });
        // This allows for the ability to override the domain used to make MfsService requests in order
        // to facilitate testing of the MfsService.
        try {
            if (window === null || window === void 0 ? void 0 : window.sessionStorage) {
                const val = window.sessionStorage.getItem('MfsServiceDomain');
                if (val) {
                    domain = val;
                }
            }
        }
        catch (error) {
            logger.logVerbose('getMfsServiceDomain', 'Session storage not found', { error });
            // Session storage may not be accessible for a variety of reasons (e.g. Content Settings),
            // ignore and move on, since we can't critically depend on it.
            //
            // We do not want to log here because their are common browser configurations (Chrome with
            // 3rd party cookies disabled) that would result in the value of sessionsStorage being
            // truthy, but throwing an exception when you attempted to access sessionStorage.
            //   https://stackoverflow.com/questions/58152236/what-to-do-when-sessionstorage-access-denied-due-to-browser-settings
            //The domain is being returned nevertheless, so there is no need to throw an error and stop the flow of the code here.
            //The try block only checks to see if there is a value of the domain in the session storage of the browser but if its not,
            //then it defaults to the specified domain. However, a verbose log statement is included to highlight this instead of an error log.
        }
        return domain;
    });
}
/**
 * Verify if there is a specified domain, if not returns the fallback domain
 *
 * @param fallbackDomain - Default domain
 * @param specifiedDomain - Should not be empty or only white spaces
 * @returns Returns the proper domain
 */
export function getDomain(fallbackDomain, specifiedDomain) {
    return logVerbose(logger, 'getDomain', () => {
        specifiedDomain = specifiedDomain === null || specifiedDomain === void 0 ? void 0 : specifiedDomain.trim();
        const domain = !(specifiedDomain === null || specifiedDomain === void 0 ? void 0 : specifiedDomain.length) ? fallbackDomain : specifiedDomain;
        logger.logVerbose('getDomain', 'Returns', { domain });
        return domain;
    });
}
function fetchWithRetry(input, init) {
    return logVerboseAsync(logger, 'fetchWithRetry', async () => {
        return new Promise((resolve, reject) => {
            const operation = retry.operation({
                retries: MAX_RETRY_AMOUNT,
                minTimeout: MIN_RETRY_TIMEOUT_MS,
            });
            operation.attempt(async (currentAttempt) => {
                try {
                    let response;
                    if (IS_BROWSER_ENVIRONMENT) {
                        response = await window.fetch(input, init);
                    }
                    else {
                        response = await nodeFetch.default(input, init);
                    }
                    logger.logVerbose('fetchWithRetry', `Response obtained with status code ${response.status} in ${IS_BROWSER_ENVIRONMENT ? 'browser' : 'node'} environment`);
                    // 429: Too Many Requests, 503: Service Unavailable, 509: Bandwidth Limit Exceeded
                    if (currentAttempt <= MAX_RETRY_AMOUNT &&
                        (response.status === 429 || response.status === 503 || response.status === 509)) {
                        logger.logVerbose('fetchWithRetry', 'Retrying', { status: response.status, currentAttempt });
                        operation.retry(new Error(response.statusText));
                        return;
                    }
                    logger.logVerbose('fetchWithRetry', 'Success', { status: response.status });
                    resolve(response);
                }
                catch (error) {
                    operation.stop();
                    logger.logVerbose('fetchWithRetry', `Failed`, { error });
                    reject(error);
                }
            });
        });
    });
}
//# sourceMappingURL=mfsService.js.map